import React, { useState } from 'react'
import styled from 'styled-components'
import { useController } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { onBackClickCallback, onNextClickType } from 'components/types'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { RadioButton } from '../core/fields/RadioButton'
import * as Yup from 'yup'
import { FormScreen, FormValues } from '../screen-templates/FormScreen'
import { Menoscale, Quizzes } from '../../redux/quiz/types'
import { DSInput, DSSpacer } from '@zoe/ds-web'
import { InputHeightScreenBody, heightValidationSchema } from '../funnel/InputHeightScreen'
import { simpleWeightValidationSchema } from '../funnel/InputWeightScreen'
import { WeightScreenFormBody } from '../screen-components/WeightScreenFormBody'
import { getDefaultWeightUnitByCountry, getFormattedWeightValues } from '../../libs/weight/weight-helpers'
import { getDefaultHeightUnitByCountry, getFormattedHeightValues } from '../../libs/height/height-helpers'
import { selectQuizCountry } from '../../redux/quiz/slice'
import { useSelector } from 'react-redux'

type Question = {
  question: string
  questionKey: keyof Menoscale
  options: Option[]
}

type Option = {
  label: string
  value: string
}

export type MenoScaleAboutMeScreenProps = {
  title: string
  description: string
  questions: Question[]
}

type Props = MenoScaleAboutMeScreenProps & {
  onNextClick: onNextClickType
  onBackClick: onBackClickCallback
  quiz: Quizzes
}

const AgeInputWrapper = styled.div`
  width: 50%;
  min-width: 150px;
`

const AgeInput = ({ name }: { name: string }) => {
  const { field, fieldState } = useController({ name })
  return (
    <AgeInputWrapper>
      <DSInput
        id="age"
        label="Your age"
        name={field.name}
        value={field.value ?? ''}
        onChange={field.onChange}
        onBlur={field.onBlur}
        type="number"
        placeholder=""
        suffix="years"
        testId="age-input"
        errorMessage={fieldState.isTouched && fieldState.error ? fieldState.error.message : undefined}
      />
    </AgeInputWrapper>
  )
}

export const MenoScaleAboutMeScreen: React.FC<Props> = (props: Props) => {
  const intl = useIntl()
  const quizCountry = useSelector(selectQuizCountry)
  const defaultHeightUnit = getDefaultHeightUnitByCountry(quizCountry ?? 'GB')
  const defaultWeightUnit = getDefaultWeightUnitByCountry(quizCountry ?? 'GB')
  const [selectedHeightUnit, setSelectedHeightUnit] = useState<string>(defaultHeightUnit)
  const [selectedWeightUnit, setSelectedWeightUnit] = useState<string>(defaultWeightUnit)

  const handleHeightUnitChange = (unit: string | ((prevState: string) => string)) => {
    const resolvedUnit = typeof unit === 'function' ? unit(selectedHeightUnit) : unit
    setSelectedHeightUnit(resolvedUnit)
    setSelectedWeightUnit(resolvedUnit === 'cm' ? 'kg' : 'stone')
  }

  const formBody = props.questions.map((question, index) => (
    <React.Fragment key={index}>
      <div style={{ marginBottom: 'var(--grid-24)', marginTop: 'var(--grid-24)' }}>
        <ContextAwareText text={question.question} quiz={props.quiz} />
      </div>
      {question.options.map((option, optionIndex) => (
        <React.Fragment key={optionIndex}>
          <RadioButton
            key={optionIndex}
            name={question.questionKey}
            id={option.value}
            value={option.value}
            label={option.label}
          />
          {optionIndex < question.options.length - 1 && <div style={{ margin: '0 4px' }} />}
        </React.Fragment>
      ))}
    </React.Fragment>
  ))

  const handleOnNextClick = (values?: FormValues) => {
    if (values) {
      const valuesFormattedForWeight = getFormattedWeightValues(values)
      const valuesFormattedForHeight = getFormattedHeightValues(valuesFormattedForWeight)
      props.onNextClick(valuesFormattedForHeight)
    } else {
      props.onNextClick(values)
    }
  }

  const validationSchemaEntries = props.questions.map((question) => [question.questionKey, Yup.string().required()])

  return (
    <FormScreen
      screenTitle={props.title}
      description={props.description}
      buttonText="Continue"
      form={{
        body: (
          <>
            <AgeInput name="age" />
            <DSSpacer size={16} direction="vertical" />
            <div style={{ width: '75%', minWidth: '320px' }}>
              <InputHeightScreenBody
                selectedUnit={selectedHeightUnit}
                onSelect={handleHeightUnitChange}
                label="Your height"
              />
              <DSSpacer size={16} direction="vertical" />
            </div>
            <div style={{ width: '75%', minWidth: '320px' }}>
              <WeightScreenFormBody
                defaultWeightUnit={defaultWeightUnit}
                selectedUnit={selectedWeightUnit}
                onUnitChange={(unit) => setSelectedWeightUnit(unit)}
                label="Your weight"
              />
            </div>
            {formBody}
          </>
        ),
        initialValues: {
          age: '',
          height_ft: defaultHeightUnit === 'ft' ? '' : undefined,
          height_in: defaultHeightUnit === 'ft' ? '' : undefined,
          height_cm: defaultHeightUnit === 'cm' ? '' : undefined,
          height_unit: defaultHeightUnit,
          weight_kg: defaultWeightUnit === 'kg' ? '' : undefined,
          weight_lbs: defaultWeightUnit === 'lbs' ? '' : undefined,
          weight_st_lbs: defaultWeightUnit === 'stone' ? '' : undefined,
          weight_st: defaultWeightUnit === 'stone' ? '' : undefined,
          weight_unit: defaultWeightUnit,
        } as FormValues,
        validationSchema: Yup.object({
          ...Object.fromEntries(validationSchemaEntries),
          ...heightValidationSchema,
          ...simpleWeightValidationSchema(intl),
          age: Yup.number()
            .typeError('Age must be a number')
            .min(18, 'Age must be at least 18')
            .max(120, 'Age must be at most 120'),
        }),
      }}
      onNextClick={handleOnNextClick}
    />
  )
}
